import React, { useEffect, useState } from "react";
import { Paperclip, MessageSquare, CheckSquare, Clock, Bell } from "lucide-react";

/**
 * Simplified colorful task card:
 *  - Task name in CAPITAL LETTERS as the header (no "TASK 01" prefix)
 *  - Flag + comment-count bell notification (bell dismisses when task opens)
 *  - Optional description (2-3 lines)
 *  - Assignee/owner text (NO photo)
 *  - Conditional icon chips: paperclip / comments / checklist / clock — only if data exists
 *  - Latest comment preview: "Author: text"
 *  - Task-XXX code + due date
 */
export default function TaskCard({ task, onOpen, onDragStart, columnColor, showProject = false, hideIssueChip = false }) {
  const commentsCount = (task.comments || []).length;
  const attachCount = (task.attachments || []).length;
  const checklist = task.checklist || task.subtasks || [];
  const checklistCount = Array.isArray(checklist) ? checklist.length : 0;
  const hasDue = !!(task.due || task.end_date);

  // Overdue detection: due date is in the past AND task is not completed.
  const isOverdue = (() => {
    if (!hasDue) return false;
    const status = String(task.status || "").toLowerCase();
    if (status === "done" || status === "completed") return false;
    const dueRaw = task.due || task.end_date;
    const d = new Date(dueRaw);
    if (isNaN(d.getTime())) return false;
    const now = new Date();
    // Compare by day (ignore time) — task is overdue if due date is before today.
    const dueDay = new Date(d.getFullYear(), d.getMonth(), d.getDate());
    const today  = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    return dueDay < today;
  })();

  const title = (task.title || task.name || "").toString();
  const description = task.description || task.desc || "";
  const priority = (task.priority || "").toLowerCase();

  // Priority chip styling — colorful pill shown on the card only when the
  // priority is meaningful (non-empty, non-default).
  const PRIORITY_META = {
    urgent: { label: "URGENT", bg: "#ef4444", fg: "#ffffff" },
    high:   { label: "HIGH",   bg: "#f97316", fg: "#ffffff" },
    medium: { label: "MEDIUM", bg: "#f59e0b", fg: "#ffffff" },
    normal: { label: "NORMAL", bg: "#0ea5e9", fg: "#ffffff" },
    low:    { label: "LOW",    bg: "#10b981", fg: "#ffffff" },
  };
  const priorityMeta = PRIORITY_META[priority] || null;

  // Auto-derive issue flag from task tags/category (chip hidden inside the
  // Issues tab itself where the label would be redundant).
  const isIssue = !hideIssueChip && (() => {
    const tags = (task.tags || []).map(x => String(x).toLowerCase());
    if (tags.some(t => /issue|bug|blocker|incident/.test(t))) return true;
    const cat = String(task.category || task.category_tag || "").toLowerCase();
    if (/issue|bug|blocker|incident/.test(cat)) return true;
    return false;
  })();

  // "Seen up to" count — persisted locally per task so the bell disappears once
  // the user opens the task (or the bell) and returns until a new comment arrives.
  const storageKey = `hexa:task-bell-seen:${task.id}`;
  const [seenCount, setSeenCount] = useState(() => {
    try {
      const v = localStorage.getItem(storageKey);
      return v == null ? 0 : parseInt(v, 10) || 0;
    } catch {
      return 0;
    }
  });
  useEffect(() => {
    // If comments decreased (deleted), keep seen aligned.
    if (seenCount > commentsCount) {
      try { localStorage.setItem(storageKey, String(commentsCount)); } catch (_) { /* ignore */ }
      setSeenCount(commentsCount);
    }
  }, [commentsCount, seenCount, storageKey]);

  const unseenCount = Math.max(0, commentsCount - seenCount);

  const markSeen = () => {
    try { localStorage.setItem(storageKey, String(commentsCount)); } catch (_) { /* ignore */ }
    setSeenCount(commentsCount);
  };

  const assigneeName = (() => {
    const list = task.assignees || [];
    if (list.length && (list[0].name || list[0].initials)) return displayName(list[0].name || list[0].initials);
    return displayName(task.owner_name || task.owner || "");
  })();

  // Latest comment: pick the newest one by created timestamp if available, else last in array.
  const latestComment = (() => {
    const list = task.comments || [];
    if (!list.length) return null;
    const sorted = [...list].sort((a, b) => {
      const ta = new Date(a.created || a.createdAt || 0).getTime();
      const tb = new Date(b.created || b.createdAt || 0).getTime();
      return tb - ta;
    });
    return sorted[0];
  })();
  const latestCommentText = latestComment
    ? stripTags(latestComment.text || latestComment.body || "").trim()
    : "";
  const latestCommentAuthor = latestComment
    ? displayName(latestComment.author_name || latestComment.author || "")
    : "";

  return (
    <div
      draggable
      onDragStart={(e) => onDragStart?.(e, task.id)}
      onClick={() => {
        markSeen();
        onOpen?.(task);
      }}
      data-testid={`task-card-${task.id}`}
      className={`tp-card group relative bg-white rounded-md shadow-sm border hover:shadow-md transition cursor-pointer overflow-hidden ${isOverdue ? "border-red-500 ring-1 ring-red-500/40" : "border-slate-200"}`}
      style={{ borderTop: `3px solid ${isOverdue ? "#ef4444" : (columnColor || "#94a3b8")}` }}
    >
      {/* Header row: task name (uppercase) + flag + bell notification */}
      <div className="px-3 pt-3 pb-1 flex items-start justify-between gap-2">
        <div
          className="text-slate-900 text-sm font-extrabold tracking-wide uppercase leading-snug flex-1 line-clamp-2"
          title={title}
        >
          {title || "UNTITLED TASK"}
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          {isOverdue && (
            <span
              data-testid={`task-card-${task.id}-overdue`}
              className="text-[9px] font-extrabold tracking-wider px-1.5 py-0.5 rounded shadow-sm animate-pulse"
              style={{ background: "#ef4444", color: "#ffffff" }}
              title="This task is overdue"
            >
              OVERDUE
            </span>
          )}
          {isIssue && (
            <span
              data-testid={`task-card-${task.id}-issue-chip`}
              className="text-[9px] font-extrabold tracking-wider px-1.5 py-0.5 rounded shadow-sm"
              style={{ background: "#8b5cf6", color: "#ffffff" }}
              title="This task is an issue"
            >
              ISSUE
            </span>
          )}
          {priorityMeta && (
            <span
              data-testid={`task-card-${task.id}-priority`}
              className="text-[9px] font-extrabold tracking-wider px-1.5 py-0.5 rounded shadow-sm"
              style={{ background: priorityMeta.bg, color: priorityMeta.fg }}
              title={`Priority: ${priorityMeta.label}`}
            >
              {priorityMeta.label}
            </span>
          )}
          {unseenCount > 0 && (
            <button
              data-testid={`task-card-${task.id}-comment-notif`}
              title={`${unseenCount} new comment${unseenCount === 1 ? "" : "s"}`}
              onClick={(e) => { e.stopPropagation(); markSeen(); }}
              className="inline-flex items-center gap-0.5 rounded-full bg-rose-500 text-white text-[10px] font-bold px-1.5 py-0.5 shadow hover:bg-rose-600 transition"
            >
              <Bell size={9} strokeWidth={2.5} />
              {unseenCount}
            </button>
          )}
        </div>
      </div>

      {/* Description — 1 line only */}
      {description && (
        <div
          className="hexa-clamp-1 px-3 pb-2 text-slate-500 text-[11px] leading-snug"
          title={stripTags(description)}
        >
          {description}
        </div>
      )}

      {/* Assignee text + conditional icon chips */}
      {(assigneeName || attachCount > 0 || commentsCount > 0 || checklistCount > 0 || hasDue) && (
        <div className="px-3 pb-2 flex items-center gap-2 flex-wrap">
          {assigneeName && (
            <div
              className="text-slate-700 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-slate-100"
              title={`Assigned to ${assigneeName}`}
            >
              {assigneeName}
            </div>
          )}
          <div className="flex items-center gap-1 flex-wrap">
            {attachCount > 0 && <IconChip icon={<Paperclip size={9} />} label={attachCount} testId={`chip-attach-${task.id}`} />}
            {commentsCount > 0 && <IconChip icon={<MessageSquare size={9} />} label={commentsCount} testId={`chip-comment-${task.id}`} />}
            {checklistCount > 0 && <IconChip icon={<CheckSquare size={9} />} label={checklistCount} testId={`chip-check-${task.id}`} />}
            {hasDue && <IconChip icon={<Clock size={9} />} testId={`chip-clock-${task.id}`} />}
          </div>
        </div>
      )}

      {/* Latest comment preview — 1 line only */}
      {latestCommentText && (
        <div
          className="hexa-clamp-1 mx-3 mb-2 px-2 py-1.5 rounded bg-slate-50 border-l-2 border-sky-400 text-[11px] text-slate-600 leading-snug"
          title={`${latestCommentAuthor}: ${latestCommentText}`}
          data-testid={`task-card-${task.id}-latest-comment`}
        >
          <span className="font-semibold text-slate-800">{latestCommentAuthor || "Someone"}:</span>{" "}
          {latestCommentText}
        </div>
      )}

      {/* Task code + project + due date */}
      <div className="px-3 pb-3 flex items-center justify-between gap-2">
        <div className="text-slate-500 text-xs font-medium truncate flex-1 min-w-0">
          <span>{task.task_code || `Task-${(task.id || "").slice(0, 3)}`}</span>
          {showProject && task.project_name && (
            <>
              <span className="mx-1.5 text-slate-300">|</span>
              <span className="uppercase tracking-wide text-slate-600" title={task.project_name}>
                {task.project_name}
              </span>
            </>
          )}
        </div>
        {hasDue && (
          <div className={`text-[11px] font-semibold tracking-wide shrink-0 ${isOverdue ? "text-red-600" : "text-slate-600"}`}>
            {formatDate(task.due || task.end_date)}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Resolve a raw identifier (email or handle) to a friendly display name.
 *  - "info@hexawebos.com" -> "HEXA Admin"
 *  - "@pradeep"           -> "Pradeep"
 *  - "john.doe@x.com"     -> "John Doe"
 */
function displayName(raw) {
  if (!raw) return "";
  let s = String(raw).trim();
  if (s.startsWith("@")) s = s.slice(1);
  if (s.includes("@")) s = s.split("@")[0];
  if (s.toLowerCase() === "info") return "HEXA Admin";
  return s
    .split(/[._\-\s]+/)
    .filter(Boolean)
    .map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");
}

function IconChip({ icon, label, testId }) {
  return (
    <div
      data-testid={testId}
      className="min-w-[24px] h-6 px-1.5 rounded-full bg-sky-500 text-white flex items-center justify-center gap-0.5 text-[9px] font-bold shadow-sm"
    >
      {icon}
      {label != null && label !== "" && <span>{label}</span>}
    </div>
  );
}

function stripTags(html) {
  if (!html) return "";
  const s = String(html).replace(/<[^>]*>/g, "");
  return s.replace(/&nbsp;/g, " ").replace(/&amp;/g, "&").replace(/&lt;/g, "<").replace(/&gt;/g, ">");
}

function formatDate(d) {
  if (!d) return "";
  try {
    const dt = new Date(d);
    if (isNaN(dt.getTime())) return String(d);
    const day = String(dt.getDate()).padStart(2, "0");
    const mon = dt.toLocaleString("en-US", { month: "short" }).toUpperCase();
    const yr = dt.getFullYear();
    return `${day}-${mon}-${yr}`;
  } catch {
    return String(d);
  }
}
